import { useMutation, useQuery } from '@tanstack/react-query';
import { Button } from '@eriko/ui/src/components/ui/button';
import { toast } from 'sonner';
import {
  createCheckoutSession,
  createPortalSession,
  getBillingStatus,
} from '../api/billing';

/**
 * Self-contained billing card. Used by:
 *   - /billing page (full-page surface for Stripe Checkout return + entitlement redirects)
 *   - Settings → Billing tab (in-context management without leaving Settings)
 *
 * Both surfaces share the same query/mutation logic so subscribe + manage flows
 * stay consistent across entry points.
 */
export function BillingPanel() {
  const { data, refetch } = useQuery({
    queryKey: ['billing', 'status'],
    queryFn: getBillingStatus,
  });

  const checkout = useMutation({
    mutationFn: createCheckoutSession,
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: () => toast.error("Couldn't start checkout. Please try again."),
  });

  const portal = useMutation({
    mutationFn: createPortalSession,
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: () => toast.error("Couldn't open billing portal."),
  });

  if (!data) return <div className="text-sm text-surface-500">Loading…</div>;

  const isPaid =
    data.subscriptionStatus === 'active' || data.subscriptionStatus === 'trialing';

  return (
    <div className="space-y-6">
      {isPaid ? (
        <div className="space-y-4 rounded-md border border-surface-200 bg-white p-6">
          <h2 className="text-lg font-semibold">Eriko Bookkeeping — $99/month</h2>
          <p className="text-sm text-surface-700">
            Your subscription is active. Next charge:{' '}
            {data.currentPeriodEnd
              ? new Date(data.currentPeriodEnd).toLocaleDateString()
              : '—'}
            .
          </p>
          <Button onClick={() => portal.mutate()} disabled={portal.isPending}>
            Manage billing
          </Button>
        </div>
      ) : (
        <div className="space-y-4 rounded-md border border-surface-200 bg-white p-6">
          <h2 className="text-lg font-semibold">Eriko Bookkeeping — $99/month</h2>
          <p className="text-sm text-surface-700">
            {data.daysLeft !== null && data.daysLeft > 0
              ? `${data.daysLeft} days left in your free trial.`
              : 'Your free trial has ended. Subscribe to resume.'}
          </p>
          <ul className="text-sm text-surface-700 list-disc pl-5 space-y-1">
            <li>Unlimited statements + receipts (up to 200 transactions/month).</li>
            <li>Monthly close reviewed by a human bookkeeper.</li>
            <li>P&amp;L, balance sheet, expense reports — anytime.</li>
          </ul>
          <Button onClick={() => checkout.mutate()} disabled={checkout.isPending} size="lg">
            Subscribe — $99/month
          </Button>
        </div>
      )}

      <button
        type="button"
        className="text-xs text-surface-500 underline"
        onClick={() => refetch()}
      >
        Refresh status
      </button>
    </div>
  );
}
